import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-display text-base font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-secondary text-secondary-foreground rounded-lg hover:bg-[#949C58] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(148,156,88,0.3)]",
        destructive:
          "bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 hover:-translate-y-0.5",
        outline:
          "border-2 border-secondary bg-transparent text-primary rounded-lg hover:bg-[#949C58] hover:text-secondary-foreground hover:-translate-y-0.5",
        secondary:
          "border-2 border-secondary bg-transparent text-primary rounded-lg hover:bg-[#949C58] hover:text-secondary-foreground hover:-translate-y-0.5",
        accent:
          "bg-[hsl(var(--accent-orange))] text-white rounded-lg hover:bg-[hsl(var(--accent-orange-hover))] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(240,105,9,0.3)]",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-lg",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "px-8 py-4",
        sm: "px-6 py-3 text-sm rounded-md",
        lg: "px-10 py-5 text-lg rounded-lg",
        icon: "h-10 w-10 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
